import React, { useState } from 'react';
import { X } from 'lucide-react';
import { AuditType } from '../types';
import { auditService } from '../auditService';

interface CreateAuditModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateAuditModal: React.FC<CreateAuditModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const [title, setTitle] = useState('');
  const [auditType, setAuditType] = useState<AuditType>('INTERNAL');
  const [department, setDepartment] = useState('Ground Operations');
  const [location, setLocation] = useState('Istanbul Main Base (LTFM)');
  const [scopeAndCriteria, setScopeAndCriteria] = useState('');
  const [plannedStart, setPlannedStart] = useState('2026-04-01');
  const [plannedEnd, setPlannedEnd] = useState('2026-04-10');
  const [leadAuditorId, setLeadAuditorId] = useState('AUDITOR-KEMAL');
  const [leadAuditorDepartment, setLeadAuditorDepartment] = useState('Quality Assurance');
  const [leadAuditorName, setLeadAuditorName] = useState('Kemal Yilmaz');
  const [auditeeFocal, setAuditeeFocal] = useState('CHIEF_GROUND_OPS');
  const [riskScore, setRiskScore] = useState(2);
  const [loading, setLoading] = useState(false);

  const isSelfAudit =
    department.trim().toLowerCase() === leadAuditorDepartment.trim().toLowerCase();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSelfAudit) {
      alert(
        `Segregation of Duties (SoD) Conflict: Lead auditor cannot audit their own department (${department})!`
      );
      return;
    }

    try {
      setLoading(true);
      await auditService.createAudit({
        title,
        auditType,
        department,
        location,
        scopeAndCriteria,
        plannedStart,
        plannedEnd,
        leadAuditorId,
        leadAuditorDepartment,
        leadAuditorName,
        auditeeFocal,
        riskScore: Number(riskScore),
        createdBy: 'QUALITY_MANAGER',
      });
      onSuccess();
    } catch (err: any) {
      alert('Failed to schedule audit: ' + (err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-lg">
        <div className="modal-header">
          <div className="modal-header-content">
            <h3>Schedule Quality / Safety Audit</h3>
            <p>
              Plan internal, supplier, regulatory, or technical audits with Segregation of Duties (SoD) enforcement.
            </p>
          </div>
          <button
            onClick={onClose}
            className="modal-close-btn"
            title="Kapat"
            aria-label="Kapat"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div className="form-field">
              <label>Audit Title *</label>
              <input
                type="text"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Ground Operations Ramp Safety & De-icing Compliance Audit"
              />
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Audit Type</label>
                <select
                  value={auditType}
                  onChange={(e) => setAuditType(e.target.value as AuditType)}
                >
                  <option value="INTERNAL">Internal Audit</option>
                  <option value="SUPPLIER">Supplier / Third-Party Audit</option>
                  <option value="REGULATORY">Regulatory Authority Audit</option>
                  <option value="PRODUCT">Product Audit</option>
                  <option value="PROCESS">Process Audit</option>
                  <option value="SYSTEM">System Audit</option>
                </select>
              </div>

              <div className="form-field">
                <label>Risk Score (1 - 5)</label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  value={riskScore}
                  onChange={(e) => setRiskScore(Number(e.target.value))}
                />
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Audited Department *</label>
                <input
                  type="text"
                  required
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                  placeholder="e.g. Ground Operations"
                />
              </div>

              <div className="form-field">
                <label>Audit Location / Station *</label>
                <input
                  type="text"
                  required
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="e.g. Istanbul Main Base (LTFM)"
                />
              </div>
            </div>

            {/* Independence Warning Box */}
            {isSelfAudit && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', fontSize: '13px', color: '#b91c1c', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>⚠️</span>
                <span>
                  <strong>Segregation of Duties (SoD) Violation:</strong> Lead auditor department ({leadAuditorDepartment}) cannot be identical to audited department ({department})!
                </span>
              </div>
            )}

            <div className="form-grid-2">
              <div className="form-field">
                <label>Lead Auditor Department *</label>
                <input
                  type="text"
                  required
                  value={leadAuditorDepartment}
                  onChange={(e) => setLeadAuditorDepartment(e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>Lead Auditor Name *</label>
                <input
                  type="text"
                  required
                  value={leadAuditorName}
                  onChange={(e) => setLeadAuditorName(e.target.value)}
                />
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-field">
                <label>Planned Start Date *</label>
                <input
                  type="date"
                  required
                  value={plannedStart}
                  onChange={(e) => setPlannedStart(e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>Planned End Date *</label>
                <input
                  type="date"
                  required
                  value={plannedEnd}
                  onChange={(e) => setPlannedEnd(e.target.value)}
                />
              </div>
            </div>

            <div className="form-field">
              <label>Scope & Regulatory Criteria *</label>
              <textarea
                required
                rows={3}
                value={scopeAndCriteria}
                onChange={(e) => setScopeAndCriteria(e.target.value)}
                placeholder="e.g. EASA ORO.GEN, IOSA GRH 1.2 ramp handling and ramp safety inspection."
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || isSelfAudit}
              className="btn-primary"
              style={{ width: 'auto' }}
            >
              {loading ? 'Scheduling...' : 'Schedule Audit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateAuditModal;
